"use client"

import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react"
import type { components } from "umbraco"
import ContactModal from "./index"
import { resolveChannels } from "./channels"

type ContactModalProperties =
  components["schemas"]["ContactModalPropertiesModel"]

interface ContactModalContextValue {
  /** Open the modal. No-op when the modal is not enabled. */
  open: () => void
  /** Close the modal. */
  close: () => void
  /** Whether the modal is currently open. */
  isOpen: boolean
  /** Whether the modal has enough CMS content to be usable. */
  isEnabled: boolean
}

const ContactModalContext = createContext<ContactModalContextValue | null>(null)

/**
 * The modal is enabled when CMS data exists AND there is either a title
 * or at least one enabled channel to show.
 */
function computeIsEnabled(data: ContactModalProperties | null): boolean {
  if (!data) return false
  return !!data.title?.trim() || resolveChannels(data).length > 0
}

interface ContactModalProviderProps {
  data: ContactModalProperties | null
  children: ReactNode
}

export const ContactModalProvider: FC<ContactModalProviderProps> = ({
  data,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const isEnabled = useMemo(() => computeIsEnabled(data), [data])

  const open = useCallback(() => {
    if (isEnabled) setIsOpen(true)
  }, [isEnabled])

  const close = useCallback(() => setIsOpen(false), [])

  const value = useMemo<ContactModalContextValue>(
    () => ({ open, close, isOpen, isEnabled }),
    [open, close, isOpen, isEnabled],
  )

  return (
    <ContactModalContext.Provider value={value}>
      {children}
      <ContactModal open={isOpen} onClose={close} data={data} />
    </ContactModalContext.Provider>
  )
}

/**
 * Access the contact modal controls from any descendant of
 * ContactModalProvider. Falls back to a disabled no-op outside the provider.
 */
export function useContactModal(): ContactModalContextValue {
  const ctx = useContext(ContactModalContext)
  if (!ctx) {
    return {
      open: () => {},
      close: () => {},
      isOpen: false,
      isEnabled: false,
    }
  }
  return ctx
}
